import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { useSelector } from 'react-redux';
import styles from './FreeGiftAOVItem.module.scss';
import chevronDownBigSvg from '../../../../assets/images/svg/chevron-down-big.svg';
import messages from './messages';
import { DetailsSectionType, GiftItem } from '../types';
import { PDPInStore, Node } from '../../../../slices/pdp/types';

const FreeGiftAOVItem = (
  {
    item
  }: {
    item: GiftItem;
  }
) => {
  const { sanitize } = DOMPurify;
  const { formatMessage } = useIntl();
  const [detailsSectionsState, setDetailsSectionsState] = useState<DetailsSectionType>({});
  const { productsSpecsAndMaterials } = useSelector((state: { pdp: PDPInStore }) => state.pdp);

  const itemInfo = useMemo(
    () =>
      productsSpecsAndMaterials.data?.find(
        (pdp) => pdp.attributes.field_product_key === item.productKey,
      ),
    [item.productKey, productsSpecsAndMaterials],
  );

  const accordionItems = useMemo(
    () =>
      itemInfo?.relationships?.field_specs_and_materials?.data
        ?.map((node: Node) =>
          productsSpecsAndMaterials.included?.find(
            (iNode: Node) => iNode.id === node.id,
          ),
        )
        ?.filter((node): node is Node => node !== undefined),
    [itemInfo, productsSpecsAndMaterials],
  );

  const getAccordionItemContent = (accordionItem: Node) => {
    const paragraphs = accordionItem.relationships.field_p_items?.data
      ?.map((node: Node) =>
        productsSpecsAndMaterials.included?.find(
          (iNode: Node) => iNode.id === node.id,
        ),
      )
      ?.filter((node): node is Node => node !== undefined);

    return paragraphs.map((paragraph) => (
      <div key={paragraph.id}>
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitize(paragraph.attributes?.field_heading?.processed),
          }}
          className={styles['paragraph-title']}
        />
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitize(
              paragraph.attributes?.field_description?.processed,
            ),
          }}
        />
      </div>
    ));
  };


  return (
    <div className={styles.container}>
      <div className={styles['bottom-section']}>
          {accordionItems?.map((accordionItem) => (
            <div
              className={styles['product-details-section']}
              key={accordionItem.id}
            >
              <div className={styles['product-details-toggle']}>
                <div
                  className={styles['product-details-section-title']}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: sanitize(
                      accordionItem.attributes?.field_label?.processed,
                    ),
                  }}
                />
                <button
                  className={classNames(
                    styles['product-details-section-toggle'],
                    {
                      [styles.opened]: detailsSectionsState[accordionItem.id],
                    },
                  )}
                  type="button"
                  onClick={() =>
                    setDetailsSectionsState((prev) => ({
                      ...prev,
                      [accordionItem.id]: !prev[accordionItem.id],
                    }))
                  }
                >
                  <img
                    src={chevronDownBigSvg as unknown as string}
                    alt={formatMessage(messages.detailsToggle)}
                  />
                </button>
              </div>
              {detailsSectionsState[accordionItem.id] && (
                <div className={styles['product-details-content']}>
                  {getAccordionItemContent(accordionItem)}
                </div>
              )}
            </div>
          ))}
        </div>
    </div>
  );
};

export default FreeGiftAOVItem;
